/**
 * KpaRatings — the year's mid-year KPA ratings as a list: the KPA on the
 * left, its rating as a badge on the right, coloured by level. Used on the
 * talent map's card and on the checklist's growth panel.
 */
import { cn } from '@/lib/utils'
import type { TalentKpaRating } from '@/services/api/talentReviewService'
import { MIDYEAR_RATING_LABEL } from './eduvosBlocks'

const TONE: Record<string, string> = {
  exceptional: 'border-emerald-200 bg-emerald-100 text-emerald-800',
  full: 'border-blue-200 bg-blue-100 text-blue-800',
  not_yet_full: 'border-amber-200 bg-amber-100 text-amber-800',
  weak: 'border-red-200 bg-red-100 text-red-800',
  tstr: 'border-gray-200 bg-gray-100 text-gray-700',
}

export default function KpaRatings({
  kpas,
  columns = 1,
  className,
}: {
  kpas: TalentKpaRating[]
  /** 2 lays the list out in two columns from md. */
  columns?: 1 | 2
  className?: string
}) {
  if (!kpas || kpas.length === 0) return null
  // An explicit 0-minimum column: the label cannot wrap, and an implicit auto
  // column would grow to its full width and push the badges out of the card
  // (the horizontal scrollbar on the person card, 8 Sep 2026).
  return (
    <ul className={cn('grid grid-cols-[minmax(0,1fr)] gap-x-4 gap-y-1', columns === 2 && 'md:grid-cols-2', className)} data-demo="talent-kpa-ratings">
      {kpas.map((k, i) => (
        <li key={`${k.name}-${i}`} className="flex min-w-0 items-center justify-between gap-3 text-xs">
          <span className="min-w-0 truncate text-gray-700" title={k.name}>
            {k.name}
          </span>
          <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium', TONE[k.rating] || TONE.tstr)}>
            {MIDYEAR_RATING_LABEL[k.rating] || k.rating}
          </span>
        </li>
      ))}
    </ul>
  )
}
